import { useState } from "react";
import { Helmet } from "react-helmet-async";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/donate`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;

const donationPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: "Donate to Sevaa Foundation",
  url: pageUrl,
  inLanguage: "en-IN",
  description:
    "Donate to Sevaa Foundation and support child education, healthcare, hunger relief, women empowerment and social welfare initiatives across India.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "Sevaa Foundation",
    url: siteUrl,
  },
  about: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "Sevaa Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
  },
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const donateActionSchema = {
  "@context": "https://schema.org",
  "@type": "DonateAction",
  "@id": `${pageUrl}#donate-action`,
  name: "Donate to Sevaa Foundation",
  description:
    "Support Sevaa Foundation's NGO initiatives for child education, healthcare, hunger relief, women empowerment and social welfare.",
  actionStatus: "https://schema.org/PotentialActionStatus",
  target: {
    "@type": "EntryPoint",
    urlTemplate: pageUrl,
    actionPlatform: [
      "https://schema.org/DesktopWebPlatform",
      "https://schema.org/MobileWebPlatform",
    ],
  },
  recipient: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "Sevaa Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    areaServed: {
      "@type": "Country",
      name: "India",
    },
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Donate",
      item: pageUrl,
    },
  ],
};

const Donate = () => {
  const [amount, setAmount] = useState<number | string>("");

  const handleDonate = (e: React.FormEvent) => {
    e.preventDefault();
    alert(
      `Backend is under construction. Thank you for initiating a donation of ₹${
        amount || 0
      }`
    );
  };

  return (
    <>
      <Helmet>
        <title>Donate to Sevaa Foundation | Support NGO Work in India</title>

        <meta
          name="title"
          content="Donate to Sevaa Foundation | Support NGO Work in India"
        />

        <meta
          name="description"
          content="Donate to Sevaa Foundation and support child education, healthcare, hunger relief, women empowerment and social welfare initiatives across India."
        />

        <meta
          name="keywords"
          content="donate to NGO India, Sevaa Foundation donation, donate for child education, healthcare NGO donation, hunger relief donation, women empowerment donation, 80G donation India, charity donation India, Swadesh Sevaa Charitable Foundation"
        />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="Sevaa Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Sevaa Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta
          property="og:title"
          content="Donate to Sevaa Foundation | Support NGO Work in India"
        />
        <meta
          property="og:description"
          content="Support Sevaa Foundation initiatives for child education, healthcare, hunger relief, women empowerment and social welfare."
        />
        <meta property="og:image" content={defaultImage} />
        <meta
          property="og:image:alt"
          content="Donate to Sevaa Foundation NGO for education healthcare and hunger relief"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Donate to Sevaa Foundation | Support NGO Work in India"
        />
        <meta
          name="twitter:description"
          content="Donate to support education, healthcare, hunger relief and social welfare initiatives by Sevaa Foundation."
        />
        <meta name="twitter:image" content={defaultImage} />
        <meta
          name="twitter:image:alt"
          content="Donate to Sevaa Foundation NGO for social welfare in India"
        />

        <script type="application/ld+json">
          {JSON.stringify(donationPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(donateActionSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-slate-50 min-h-screen py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <section
            aria-labelledby="donate-heading"
            className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col lg:flex-row"
          >
            {/* Left Panel - Trust & Info */}
            <div className="bg-slate-900 text-white p-10 lg:w-5/12 flex flex-col justify-center">
              <h1
                id="donate-heading"
                className="text-3xl font-extrabold mb-6"
              >
                Your Support Matters
              </h1>

              <p className="text-gray-300 mb-8 text-lg">
                Every rupee you donate goes directly towards field projects.
                Help us bring a positive change in the lives of the needy.
              </p>

              <div className="space-y-6" aria-label="Donation benefits">
                <article className="flex items-start gap-4">
                  <div
                    className="bg-slate-800 p-3 rounded-full text-orange-500 text-xl"
                    aria-hidden="true"
                  >
                    📄
                  </div>
                  <div>
                    <h2 className="font-bold text-lg">80G Tax Exemption</h2>
                    <p className="text-sm text-gray-400">
                      Save up to 50% on your taxes with a valid 80G receipt
                      provided instantly.
                    </p>
                  </div>
                </article>

                <article className="flex items-start gap-4">
                  <div
                    className="bg-slate-800 p-3 rounded-full text-orange-500 text-xl"
                    aria-hidden="true"
                  >
                    🔒
                  </div>
                  <div>
                    <h2 className="font-bold text-lg">100% Secure Payment</h2>
                    <p className="text-sm text-gray-400">
                      Your details and transactions are encrypted and safely
                      processed.
                    </p>
                  </div>
                </article>

                <article className="flex items-start gap-4">
                  <div
                    className="bg-slate-800 p-3 rounded-full text-orange-500 text-xl"
                    aria-hidden="true"
                  >
                    📊
                  </div>
                  <div>
                    <h2 className="font-bold text-lg">Total Transparency</h2>
                    <p className="text-sm text-gray-400">
                      Get regular email updates on how your money is being
                      utilized.
                    </p>
                  </div>
                </article>
              </div>
            </div>

            {/* Right Panel - Donation Form */}
            <div className="p-10 lg:w-7/12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 border-b pb-4">
                Make a Secure Donation
              </h2>

              <form
                onSubmit={handleDonate}
                className="space-y-6"
                aria-label="Donation form"
              >
                {/* Amount Selection */}
                <div>
                  <label
                    htmlFor="donationAmount"
                    className="block text-sm font-semibold text-gray-700 mb-3"
                  >
                    Select Amount (₹)
                  </label>

                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {[1000, 2500, 5000].map((val) => (
                      <button
                        type="button"
                        key={val}
                        onClick={() => setAmount(val)}
                        className={`py-3 rounded-lg border font-bold transition-all ${
                          amount === val
                            ? "bg-orange-500 text-white border-orange-500"
                            : "bg-white text-slate-700 border-gray-300 hover:border-orange-400"
                        }`}
                        aria-pressed={amount === val}
                        aria-label={`Select donation amount rupees ${val}`}
                      >
                        ₹{val}
                      </button>
                    ))}
                  </div>

                  <input
                    id="donationAmount"
                    name="donationAmount"
                    type="number"
                    placeholder="Or enter custom amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                    min="1"
                    inputMode="numeric"
                    required
                  />
                </div>

                {/* Personal Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm font-semibold text-gray-700 mb-1"
                    >
                      Full Name
                    </label>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      autoComplete="name"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-gray-700 mb-1"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="phoneNumber"
                      className="block text-sm font-semibold text-gray-700 mb-1"
                    >
                      Phone Number
                    </label>
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      autoComplete="tel"
                      inputMode="tel"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="panCard"
                      className="block text-sm font-semibold text-gray-700 mb-1"
                    >
                      PAN Card (Optional)
                    </label>
                    <input
                      id="panCard"
                      name="panCard"
                      type="text"
                      placeholder="Required for 80G"
                      autoComplete="off"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-4 rounded-lg shadow-lg transition-colors text-lg flex justify-center items-center gap-2"
                >
                  Proceed to Pay <span aria-hidden="true">→</span>
                </button>
              </form>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default Donate;